import { registerNotificationKind } from "../registry";
import type { UserNotification } from "../types";

/**
 * `cr_alert`: published by the Condition Report workflow
 * (mre/products/sc_auto/cr_record_ops.py `_publish_alert`) whenever a report
 * involves the person: assigned to them or their group, moved forward or
 * sent back while they hold it, returned to them as its initiator, mentioned
 * in its discussion, or an action overdue. `data.kind` says which,
 * `data.cr_number` names the report, and `link` opens it.
 */
export type CrAlertKind =
  | "initiated"
  | "assigned"
  | "advanced"
  | "returned"
  | "mentioned"
  | "overdue"
  | "screened"
  | "reopened";

const TITLES: Record<CrAlertKind, (cr: string) => string> = {
  initiated: (cr) => `${cr} is waiting for screening`,
  assigned: (cr) => `${cr} assigned to you`,
  advanced: (cr) => `${cr} moved on`,
  returned: (cr) => `${cr} sent back to you`,
  mentioned: (cr) => `You were mentioned on ${cr}`,
  overdue: (cr) => `${cr} has overdue work`,
  screened: (cr) => `${cr} was screened by the AI`,
  reopened: (cr) => `${cr} reopened after screening`,
};

/** Kinds whose server title says more than the kind alone: a `screened`
 * alert is "Screening complete" or "Screening needs a screener", and an
 * `assigned` one may say whether the person or their group was assigned. A
 * server title that is only the CR number says nothing extra. */
const SERVER_TITLED: ReadonlySet<CrAlertKind> = new Set([
  "screened",
  "assigned",
]);

/** The server titles a group assignment only in its message today. */
const GROUP_ASSIGNMENT = /assigned to your group/i;

const crNumber = (n: UserNotification): string =>
  typeof n.data.cr_number === "string" && n.data.cr_number
    ? n.data.cr_number
    : "A condition report";

registerNotificationKind("cr_alert", {
  // Every CR alert is a finished fact, never a transient status.
  listed: () => true,
  title: (n) => {
    const kind = n.data.kind as CrAlertKind | undefined;
    const cr = crNumber(n);
    if (kind && SERVER_TITLED.has(kind) && n.title && n.title !== cr) {
      return `${cr}: ${n.title}`;
    }
    if (kind === "assigned" && GROUP_ASSIGNMENT.test(n.message ?? "")) {
      return `${cr} assigned to your group`;
    }
    return kind && TITLES[kind] ? TITLES[kind](cr) : (n.title ?? cr);
  },
  message: (n) => n.message,
  link: (n) =>
    n.link ??
    (n.resource_id ? `/products/cap-ai/cr/records/${n.resource_id}` : null),
});
